import { useState, useEffect, useMemo, useRef, useCallback } from "react";
import {
  customerServiceConversations,
  onlineAgents,
  blurredChatMessages,
  getBlurredTextsForConversation,
  TYPING_TEXT,
  CS_UNAVAILABLE_TEXT,
  CS_WARNING_TEXT,
  INPUT_PLACEHOLDER,
  SEND_BUTTON_TEXT,
  NEW_MESSAGE_PREVIEWS,
} from "@/mocks/customerService";

interface Conversation {
  id: string;
  customer: string;
  contact: string;
  status: string;
  preview: string;
  unread: number;
  priority: string;
  tags: string[];
  language: string;
  _time?: string;
  _msgCount?: number;
}

const STATUS_LABELS: Record<string, string> = {
  詢問中: "詢問中",
  處理中: "處理中",
  等待回覆: "等待回覆",
  已解決: "已解決",
  已關閉: "已關閉",
};

const STATUS_COLORS: Record<string, string> = {
  詢問中: "bg-accent-100 text-accent-700",
  處理中: "bg-primary-100 text-primary-700",
  等待回覆: "bg-secondary-100 text-secondary-700",
  已解決: "bg-secondary-50 text-secondary-600",
  已關閉: "bg-background-200 text-foreground-400",
};

const TAG_COLORS: Record<string, string> = {
  未指派: "bg-accent-50 text-accent-600",
  已指派: "bg-primary-50 text-primary-600",
  等待客戶: "bg-secondary-50 text-secondary-600",
  已結案: "bg-background-200 text-foreground-400",
  會員客: "bg-primary-50 text-primary-600",
  婚宴客: "bg-accent-50 text-accent-600",
  團體訂單: "bg-primary-50 text-primary-600",
  VIP客: "bg-primary-50 text-primary-600",
  五星飯店: "bg-primary-50 text-primary-600",
};

function getInitial(name: string): string {
  return name.charAt(0);
}

// 對話框裡的假對話最多 15 條
const MAX_FAKE_MESSAGES = 15;

function stableWidth(msgId: string, lineIdx: number, totalLines: number, convId?: string): number {
  let hash = 0;
  const str = (convId || "") + msgId + lineIdx;
  for (let i = 0; i < str.length; i++) {
    hash = ((hash << 5) - hash) + str.charCodeAt(i);
    hash |= 0;
  }
  // 最後一行偏短，模擬真實文字塊的不規則結尾
  const isLastLine = lineIdx === totalLines - 1 && totalLines > 1;
  const minW = isLastLine ? 60 : 90;
  const maxW = isLastLine ? 240 : 400;
  return minW + Math.abs(hash) % (maxW - minW);
}

function getLang(conv: Conversation | null): string {
  return conv?.language || "zh";
}

function mulberry32(seed: number): () => number {
  return () => {
    seed |= 0;
    seed = (seed + 0x6d2b79f5) | 0;
    let t = Math.imul(seed ^ (seed >>> 15), 1 | seed);
    t = (t + Math.imul(t ^ (t >>> 7), 61 | t)) ^ t;
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };
}

function seededShuffle(arr: Conversation[], seed: number): Conversation[] {
  const result = [...arr];
  const rng = mulberry32(seed);
  for (let i = result.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function getSessionSeed(): number {
  const key = "cs_shuffle_seed";
  const stored = sessionStorage.getItem(key);
  if (stored) return parseInt(stored, 10);
  const seed = Math.floor(Math.random() * 2147483647);
  sessionStorage.setItem(key, String(seed));
  return seed;
}

function buildConversationsOnce(): Conversation[] {
  const seed = getSessionSeed();
  const baseTime = new Date().getTime();
  const shuffled = seededShuffle(customerServiceConversations as Conversation[], seed);
  return shuffled.map((conv, idx) => {
    const idNum = parseInt(conv.id.replace("c", ""), 10) || 0;
    const offsetMinutes = idx * 7;
    const d = new Date(baseTime - offsetMinutes * 60000);
    const timeStr = `${String(d.getHours()).padStart(2, "0")}:${String(d.getMinutes()).padStart(2, "0")}`;
    const msgCount = 3 + (idNum % 8);
    return {
      ...conv,
      language: (conv as unknown as Record<string, unknown>).language as string || "zh",
      _time: timeStr,
      _msgCount: msgCount,
    };
  });
}

// ═══════════════════════════════════════════
// MODULE-LEVEL SIMULATION ENGINE
// 全部在 component 外面跑，切換導航不中斷
// ═══════════════════════════════════════════

let _conversationState: Conversation[] = [];
let _globalMsgRounds = 0;
let _globalBumpCounter = 0;
let _selectedIdGlobal: string | null = null;
let _typingIdsGlobal: Set<string> = new Set();
let _timersInitialized = false;

function dispatchTick() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("cs:tick"));
  }
}

function dispatchMsgTick() {
  if (typeof window !== "undefined") {
    window.dispatchEvent(new CustomEvent("cs:msg-tick"));
  }
}

function currentTimeStr(): string {
  const now = new Date();
  return `${String(now.getHours()).padStart(2, "0")}:${String(now.getMinutes()).padStart(2, "0")}`;
}

function initGlobalTimers() {
  if (_timersInitialized) return;
  _timersInitialized = true;

  _conversationState = buildConversationsOnce();

  // ① 訊息陸續出現 — 每 25 秒全體推一輪（跨導航累積）
  setInterval(() => {
    _globalMsgRounds++;
    dispatchMsgTick();
    dispatchTick();
  }, 25000);

  // ② 隨機對話置頂 — 每 10 秒
  setInterval(() => {
    if (_conversationState.length === 0) return;
    const idx = _globalBumpCounter % _conversationState.length;
    _globalBumpCounter++;
    const target = _conversationState[idx];
    if (!target) return;

    const lang = target.language || "zh";
    const langPreviews = NEW_MESSAGE_PREVIEWS[lang] || NEW_MESSAGE_PREVIEWS.zh;
    const previewIdx = _globalBumpCounter % langPreviews.length;
    const timeStr = currentTimeStr();

    const withoutTarget = _conversationState.filter((c) => c.id !== target.id);
    const bumped: Conversation = {
      ...target,
      _time: timeStr,
      preview: langPreviews[previewIdx],
      // 未讀上限 3；隨機決定本次是否 +1，避免每個對話的未讀數都變成 3
      unread: target.id === _selectedIdGlobal ? 0 : Math.min((target.unread || 0) + (Math.random() < 0.55 ? 1 : 0), 3),
      status: target.status === "已解決" || target.status === "已關閉" ? "詢問中" : target.status,
    };
    _conversationState = [bumped, ...withoutTarget];
    (_conversationState as unknown as Record<string, unknown>)._lastBumpedId = target.id;
    dispatchTick();
  }, 10000);

  // ③ 自動指派 — 每 10~30 秒遞迴
  function scheduleAutoAssign() {
    const delay = 10000 + Math.random() * 20000;
    setTimeout(() => {
      const unassigned = _conversationState.filter((c) => c.tags.includes("未指派"));
      if (unassigned.length > 0) {
        const pick = unassigned[Math.floor(Math.random() * unassigned.length)];
        _conversationState = _conversationState.map((c) =>
          c.id === pick.id
            ? {
                ...c,
                tags: c.tags.map((t) => (t === "未指派" ? "已指派" : t)),
                status: "處理中",
                _time: currentTimeStr(),
              }
            : c
        );
        (_conversationState as unknown as Record<string, unknown>)._lastAutoAssignedId = pick.id;
        dispatchTick();
      }
      scheduleAutoAssign();
    }, delay);
  }
  scheduleAutoAssign();

  // ④ 隨機打字指示器 — 每 10 秒，顯示 5 秒後消失
  setInterval(() => {
    const eligible = _conversationState.filter(
      (c) => !c.tags.includes("已結案") && c.status !== "已關閉"
    );
    if (eligible.length === 0) return;
    const count = 2 + Math.floor(Math.random() * 3);
    const shuffled = [...eligible].sort(() => Math.random() - 0.5);
    const picked = shuffled.slice(0, Math.min(count, shuffled.length));
    _typingIdsGlobal = new Set(picked.map((c) => c.id));
    dispatchTick();
    setTimeout(() => {
      _typingIdsGlobal = new Set();
      dispatchTick();
    }, 5000);
  }, 10000);
}

// 模組載入即啟動（只跑一次）
initGlobalTimers();

function getConversationsSnapshot(): Conversation[] {
  return [..._conversationState];
}

function getLastBumpedId(): string | null {
  return ((_conversationState as unknown as Record<string, unknown>)._lastBumpedId as string) || null;
}

function getLastAutoAssignedId(): string | null {
  return ((_conversationState as unknown as Record<string, unknown>)._lastAutoAssignedId as string) || null;
}

const TYPING_DOTS = [
  { id: "td1", delay: "0ms" },
  { id: "td2", delay: "200ms" },
  { id: "td3", delay: "400ms" },
];

export default function CustomerServicePage() {
  const [search, setSearch] = useState("");
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [activeTab, setActiveTab] = useState<"all" | "unread" | "assigned">("all");
  const [newMsgPulse, setNewMsgPulse] = useState<string | null>(null);
  const chatContainerRef = useRef<HTMLDivElement>(null);

  const [conversations, setConversations] = useState<Conversation[]>(getConversationsSnapshot);
  const [tick, setTick] = useState(0);
  const [msgTick, setMsgTick] = useState(0);
  const [justBumpedId, setJustBumpedId] = useState<string | null>(null);
  const [autoAssignedId, setAutoAssignedId] = useState<string | null>(null);
  const [typingIds, setTypingIds] = useState<Set<string>>(new Set());

  // 監聽全域 tick → 同步 React state
  useEffect(() => {
    const onTick = () => {
      setConversations(getConversationsSnapshot());
      setTypingIds(new Set(_typingIdsGlobal));
      setTick((t) => t + 1);

      const bumped = getLastBumpedId();
      if (bumped) {
        setJustBumpedId(bumped);
        setTimeout(() => setJustBumpedId(null), 1200);
        (_conversationState as unknown as Record<string, unknown>)._lastBumpedId = undefined;
      }

      const assigned = getLastAutoAssignedId();
      if (assigned) {
        setAutoAssignedId(assigned);
        setTimeout(() => setAutoAssignedId(null), 1500);
        (_conversationState as unknown as Record<string, unknown>)._lastAutoAssignedId = undefined;
      }
    };
    window.addEventListener("cs:tick", onTick);
    return () => window.removeEventListener("cs:tick", onTick);
  }, []);

  // 監聽訊息 tick → 打字 + 脈衝效果
  const msgRoundRef = useRef(_globalMsgRounds);
  useEffect(() => {
    const onMsgTick = () => {
      setMsgTick((t) => t + 1);
      if (_selectedIdGlobal && _globalMsgRounds > msgRoundRef.current) {
        const conv = _conversationState.find((c) => c.id === _selectedIdGlobal);
        const initial = (conv?._msgCount || 3);
        const targetIdx = Math.min(initial + _globalMsgRounds - 1, MAX_FAKE_MESSAGES - 1);
        const msg = blurredChatMessages[targetIdx];
        if (msg) {
          setNewMsgPulse(msg.id);
          setTimeout(() => setNewMsgPulse(null), 800);
        }
      }
      msgRoundRef.current = _globalMsgRounds;
    };
    window.addEventListener("cs:msg-tick", onMsgTick);
    return () => window.removeEventListener("cs:msg-tick", onMsgTick);
  }, []);

  // 同步 selectedId 到全域
  useEffect(() => {
    _selectedIdGlobal = selectedId;
  }, [selectedId]);

  const filtered = useMemo(() => {
    let list = [...conversations];
    if (activeTab === "unread") list = list.filter((c) => c.unread > 0);
    if (activeTab === "assigned") list = list.filter((c) => c.tags.includes("已指派"));
    if (search.trim()) {
      const q = search.toLowerCase();
      list = list.filter(
        (c) =>
          c.customer.toLowerCase().includes(q) ||
          c.contact.toLowerCase().includes(q) ||
          c.preview.toLowerCase().includes(q)
      );
    }
    return list;
  }, [conversations, activeTab, search]);

  const selectedConversation = useMemo(
    () => conversations.find((c) => c.id === selectedId) || null,
    [conversations, selectedId]
  );

  const stats = useMemo(() => {
    const unassigned = conversations.filter((c) => c.tags.includes("未指派")).length;
    const processing = conversations.filter((c) => c.status === "處理中").length;
    const closed = conversations.filter((c) => c.tags.includes("已結案")).length;
    return { unassigned, processing, closed };
  }, [conversations]);

  // 訊息筆數 = 初始 + 全域累計輪次（跨導航不會重置）
  const visibleMessageCount = useMemo(() => {
    if (!selectedConversation) return 3;
    const initial = (selectedConversation as unknown as Record<string, unknown>)._msgCount as number || 3;
    void msgTick; // msgTick 只負責觸發重算（_globalMsgRounds 是模組級計數器，本身不具反應性）
    return Math.min(initial + _globalMsgRounds, MAX_FAKE_MESSAGES);
  }, [selectedConversation, msgTick]);

  // 打字動畫：最近一次訊息 tick 後持續 3 秒
  const [typingActive, setTypingActive] = useState(false);
  useEffect(() => {
    if (msgTick === 0) return;
    setTypingActive(true);
    const t = setTimeout(() => setTypingActive(false), 3000);
    return () => clearTimeout(t);
  }, [msgTick]);

  const handleSelectConversation = useCallback((id: string) => {
    setSelectedId(id);
    _selectedIdGlobal = id;
    _conversationState = _conversationState.map((c) =>
      c.id === id ? { ...c, unread: 0 } : c
    );
    setConversations(getConversationsSnapshot());
  }, []);

  const onlineCount = onlineAgents.filter((a) => a.status === "online").length;

  return (
    <div className="h-full flex flex-col -m-4 md:-m-6">
      {/* 頂部欄 */}
      <div className="px-6 py-4 border-b border-background-200 flex items-center justify-between shrink-0">
        <div>
          <h1 className="text-xl font-bold text-foreground-900" style={{ fontFamily: "var(--font-heading)" }}>
            客服中心
          </h1>
          <p className="text-sm text-foreground-500">客戶聊天室即時管理</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-md">
            <i className="ri-global-line mr-1" />
            試用期體驗
          </div>
          <div className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-md">
            <i className="ri-focus-3-line mr-1" />
            繁中
          </div>
          <div className="text-xs text-foreground-500 bg-background-100 px-2.5 py-1 rounded-md">
            <i className="ri-sun-line mr-1" />
          </div>
          <div className="relative w-7 h-7 rounded-full bg-primary-100 flex items-center justify-center">
            <i className="ri-notification-3-line text-primary-500 text-xs" />
            <span className="absolute -top-0.5 -right-0.5 w-3.5 h-3.5 bg-accent-500 text-background-50 text-[9px] font-bold rounded-full flex items-center justify-center">
              3
            </span>
          </div>
          <div className="w-7 h-7 rounded-full bg-primary-500 flex items-center justify-center text-background-50 text-xs font-bold">
            我
          </div>
        </div>
      </div>

      {/* 三欄主體 */}
      <div className="flex-1 flex overflow-hidden min-h-0">
        {/* 左欄：客戶對話列表 */}
        <div className="w-[340px] shrink-0 border-r border-background-200 flex flex-col bg-background-50/50">
          {/* 搜尋 */}
          <div className="px-3 py-2.5 border-b border-background-200">
            <div className="flex items-center gap-2 px-3 py-2.5 rounded-lg bg-background-100 border border-background-200">
              <i className="ri-search-line text-foreground-400 text-sm" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="搜尋客戶 / 公司 / 主題"
                className="flex-1 bg-transparent text-sm text-foreground-700 placeholder:text-foreground-400 outline-none"
              />
              <button className="w-6 h-6 flex items-center justify-center text-foreground-400 hover:text-foreground-600 cursor-pointer">
                <i className="ri-refresh-line text-sm" />
              </button>
            </div>
            <div className="flex items-center gap-2 mt-3">
              <button
                onClick={() => setActiveTab("all")}
                className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === "all"
                    ? "bg-primary-50 text-primary-600"
                    : "text-foreground-400 hover:text-foreground-600"
                }`}
              >
                全部
              </button>
              <button
                onClick={() => setActiveTab("unread")}
                className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === "unread"
                    ? "bg-primary-50 text-primary-600"
                    : "text-foreground-400 hover:text-foreground-600"
                }`}
              >
                未讀
              </button>
              <button
                onClick={() => setActiveTab("assigned")}
                className={`flex-1 py-1.5 rounded-md text-sm font-medium transition-colors cursor-pointer whitespace-nowrap ${
                  activeTab === "assigned"
                    ? "bg-primary-50 text-primary-600"
                    : "text-foreground-400 hover:text-foreground-600"
                }`}
              >
                已指派
              </button>
            </div>
          </div>

          {/* 統計 */}
          <div className="px-3 py-2.5 border-b border-background-100 flex items-center gap-4">
            <div className="flex items-center gap-1.5">
              <div className="w-6 h-6 rounded bg-primary-100 flex items-center justify-center text-primary-500 text-xs font-bold">
                200
              </div>
              <span className="text-xs text-foreground-400">全部</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-accent-500 font-bold">●</span>
              <span className="text-xs text-foreground-400">未指派 {stats.unassigned}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-primary-500 font-bold">●</span>
              <span className="text-xs text-foreground-400">處理中 {stats.processing}</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="text-xs text-secondary-500 font-bold">●</span>
              <span className="text-xs text-foreground-400">結案 {stats.closed}</span>
            </div>
          </div>

          {/* 對話列表 */}
          <div className="flex-1 overflow-y-auto">
            {filtered.map((conv) => (
              <button
                key={conv.id}
                onClick={() => handleSelectConversation(conv.id)}
                className={`w-full text-left px-4 py-3 border-b border-background-100 transition-all cursor-pointer ${
                  selectedId === conv.id
                    ? "bg-primary-50/60 border-l-2 border-l-primary-500"
                    : justBumpedId === conv.id
                    ? "bg-accent-50/60 border-l-2 border-l-accent-400"
                    : autoAssignedId === conv.id
                    ? "bg-primary-50/40 border-l-2 border-l-primary-400"
                    : "hover:bg-background-50/80 border-l-2 border-l-transparent"
                }`}
                style={
                  justBumpedId === conv.id
                    ? { animation: "bumpIn 0.6s ease-out" }
                    : undefined
                }
              >
                <div className="flex items-start gap-2.5">
                  <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 text-xs font-bold shrink-0">
                    {getInitial(conv.customer)}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-1.5">
                      <p className="text-sm font-semibold text-foreground-800 truncate">
                        {conv.customer}
                      </p>
                      <span className="text-xs text-foreground-400">| {conv.contact}</span>
                    </div>
                    <div className="flex items-center gap-1 mt-1">
                      {conv.tags.map((tag) => (
                        <span
                          key={tag}
                          className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                            TAG_COLORS[tag] || "bg-background-200 text-foreground-400"
                          }`}
                        >
                          {tag}
                        </span>
                      ))}
                      <span
                        className={`text-[10px] px-1.5 py-0.5 rounded font-medium ${
                          STATUS_COLORS[conv.status] || "bg-background-200 text-foreground-400"
                        }`}
                      >
                        {STATUS_LABELS[conv.status]}
                      </span>
                    </div>
                    <p className="text-xs text-foreground-400 mt-1.5 truncate">
                      {typingIds.has(conv.id) ? (
                        <span className="text-accent-500 font-medium inline-flex items-center gap-1">
                          <span className="inline-flex gap-0.5">
                            <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" style={{ animationDelay: "0ms" }} />
                            <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" style={{ animationDelay: "200ms" }} />
                            <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" style={{ animationDelay: "400ms" }} />
                          </span>
                          {TYPING_TEXT.zh}
                        </span>
                      ) : (
                        conv.preview
                      )}
                    </p>
                  </div>
                  <div className="flex flex-col items-end gap-1 shrink-0">
                    <span className="text-xs text-foreground-400">{conv._time}</span>
                    {conv.unread > 0 && (
                      <span className="w-5 h-5 bg-accent-500 text-background-50 text-[10px] font-bold rounded-full flex items-center justify-center">
                        {conv.unread}
                      </span>
                    )}
                  </div>
                </div>
              </button>
            ))}
          </div>
        </div>

        {/* 中欄：聊天區域（模糊） */}
        <div className="flex-1 flex flex-col min-w-0 bg-background-50/30">
          {/* 聊天頭部 */}
          <div className="px-5 py-4 border-b border-background-200 flex items-center justify-between shrink-0">
            {selectedConversation ? (
              <>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 text-sm font-bold">
                    {getInitial(selectedConversation.customer)}
                  </div>
                  <div>
                    <div className="flex items-center gap-2">
                      <span className="text-base font-semibold text-foreground-900">
                        {selectedConversation.customer}
                      </span>
                      <span className="text-sm text-foreground-400">
                        | {selectedConversation.contact}
                      </span>
                      {typingIds.has(selectedConversation.id) ? (
                        <span className="text-[10px] text-accent-500 font-medium inline-flex items-center gap-1">
                          <span className="inline-flex gap-0.5">
                            <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" style={{ animationDelay: "0ms" }} />
                            <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" style={{ animationDelay: "200ms" }} />
                            <span className="w-1 h-1 rounded-full bg-accent-500 animate-pulse" style={{ animationDelay: "400ms" }} />
                          </span>
                          {TYPING_TEXT.zh}
                        </span>
                      ) : (
                        <span className="text-[10px] text-foreground-400">
                          • 主題：—
                        </span>
                      )}
                    </div>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <span className={`text-xs px-2.5 py-1 rounded-full font-medium ${
                    STATUS_COLORS[selectedConversation.status]
                  }`}>
                    {STATUS_LABELS[selectedConversation.status]}
                  </span>
                </div>
              </>
            ) : (
              <div className="flex items-center gap-2 text-foreground-400">
                <i className="ri-chat-3-line" />
                <span className="text-sm">選擇對話查看詳情</span>
              </div>
            )}
          </div>

          {/* 聊天內容 */}
          <div
            ref={chatContainerRef}
            className="flex-1 overflow-y-auto px-5 py-5 space-y-4 min-h-0"
          >
            {selectedConversation ? (
              <>
                <div className="flex justify-center mb-2">
                  <span className="text-[10px] font-medium text-accent-600 bg-accent-50 border border-accent-200 px-2.5 py-1 rounded-full">
                    <i className="ri-error-warning-line mr-1" />
                    {CS_UNAVAILABLE_TEXT.zh}
                  </span>
                </div>

                {blurredChatMessages.slice(0, visibleMessageCount).map((msg, idx) => {
                  const lang = getLang(selectedConversation);
                  const langTexts = getBlurredTextsForConversation(selectedConversation.id, lang);
                  const messageText = langTexts[idx] || "";
                  const isNew = newMsgPulse === msg.id;
                  return (
                    <div
                      key={msg.id}
                      className={`flex gap-3 ${msg.isLeft ? "" : "flex-row-reverse"}`}
                      style={{
                        animation: `fadeInUp 0.8s ease-out ${idx * 0.25}s both`,
                      }}
                    >
                      {msg.isLeft && (
                        <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-600 text-xs font-bold shrink-0">
                          {getInitial(selectedConversation.customer)}
                        </div>
                      )}
                      <div className={`${msg.isLeft ? "" : "text-right"}`}>
                        <div
                          className={`inline-block px-4 py-3 rounded-2xl ${msg.color} ${
                            msg.isLeft ? "rounded-tl-sm" : "rounded-tr-sm"
                          } ${isNew ? "ring-2 ring-accent-400/60" : ""}`}
                          style={{
                            filter: "blur(4px)",
                            userSelect: "none",
                            transition: "ring 0.3s ease",
                          }}
                        >
                          {messageText ? (
                            <p className="text-sm text-foreground-800 leading-relaxed">{messageText}</p>
                          ) : (
                            Array.from({ length: msg.lines }).map((_, i) => (
                              <div
                                key={i}
                                className={`h-2.5 rounded-full mb-2 last:mb-0 ${
                                  msg.color === "bg-primary-100"
                                    ? "bg-primary-300/40"
                                    : "bg-secondary-300/40"
                                }`}
                                style={{
                                  width: `${stableWidth(msg.id, i, msg.lines, selectedConversation.id)}px`,
                                }}
                              />
                            ))
                          )}
                        </div>
                      </div>
                    </div>
                  );
                })}

                {typingActive && (
                  <div className="flex gap-3" style={{ animation: "fadeInUp 0.3s ease-out" }}>
                    <div className="w-8 h-8 rounded-full bg-secondary-200 flex items-center justify-center text-secondary-600 text-xs font-bold shrink-0">
                      {getInitial(selectedConversation.customer)}
                    </div>
                    <div className="inline-flex items-center gap-1 px-4 py-3 rounded-2xl rounded-tl-sm bg-secondary-100">
                      {TYPING_DOTS.map((dot) => (
                        <span
                          key={dot.id}
                          className="w-2 h-2 rounded-full bg-secondary-400"
                          style={{
                            animation: `typingPulse 1.2s ease-in-out ${dot.delay} infinite`,
                          }}
                        />
                      ))}
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="flex flex-col items-center justify-center h-full text-foreground-400">
                <i className="ri-customer-service-2-line text-6xl mb-4 text-foreground-300" />
                <p className="text-base font-medium">選擇左側對話開始</p>
                <p className="text-sm mt-2">點擊客戶名稱查看聊天記錄</p>
              </div>
            )}
            <div className="h-4" />
          </div>

          {/* 底部警告橫幅 */}
          {selectedConversation && selectedConversation.status !== "已解決" && selectedConversation.status !== "已關閉" && (
            <div className="shrink-0">
              <div className="mx-4 mb-4 p-4 rounded-lg bg-accent-50/80 border border-accent-200/60 flex items-start gap-3">
                <i className="ri-error-warning-line text-accent-500 text-base mt-0.5" />
                <div>
                  <p className="text-sm font-semibold text-accent-700">
                    {CS_WARNING_TEXT.zh.title}
                  </p>
                  <p className="text-sm text-accent-600 mt-1">
                    {CS_WARNING_TEXT.zh.desc}
                  </p>
                </div>
              </div>

              <div className="px-5 py-4 border-t border-background-200">
                <div className="flex items-center gap-3">
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-not-allowed opacity-50">
                    <i className="ri-emotion-line text-lg" />
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-not-allowed opacity-50">
                    <i className="ri-image-line text-lg" />
                  </button>
                  <button className="w-10 h-10 flex items-center justify-center rounded-lg hover:bg-background-100 text-foreground-400 cursor-not-allowed opacity-50">
                    <i className="ri-attachment-line text-lg" />
                  </button>
                  <input
                    type="text"
                    disabled
                    placeholder={INPUT_PLACEHOLDER.zh}
                    className="flex-1 px-4 py-2.5 rounded-lg border border-background-200 bg-background-100 text-base text-foreground-400 placeholder:text-foreground-400 outline-none cursor-not-allowed"
                  />
                  <button
                    disabled
                    className="px-5 py-2.5 rounded-lg bg-background-200 text-foreground-400 text-base font-medium cursor-not-allowed opacity-50 flex items-center gap-2 whitespace-nowrap"
                  >
                    <i className="ri-send-plane-line text-lg" />
                    {SEND_BUTTON_TEXT.zh}
                  </button>
                </div>
                <div className="flex items-center justify-between mt-3">
                  <span className="text-xs text-foreground-400">
                    <i className="ri-error-warning-line mr-1" />
                    {CS_UNAVAILABLE_TEXT.zh}
                  </span>
                  <div className="flex items-center gap-3">
                    <button className="text-xs text-foreground-400 hover:text-foreground-600 cursor-not-allowed opacity-50">
                      <i className="ri-mic-line text-base" />
                    </button>
                    <button className="text-xs text-foreground-400 hover:text-foreground-600 cursor-not-allowed opacity-50">
                      <i className="ri-file-list-3-line text-base" />
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>

        {/* 右欄：在線客服 */}
        <div className="w-[280px] shrink-0 border-l border-background-200 flex flex-col bg-background-50/50">
          <div className="px-4 py-3 border-b border-background-200">
            <div className="flex items-center gap-2 mb-2">
              <i className="ri-customer-service-2-line text-primary-500 text-base" />
              <span className="text-base font-semibold text-foreground-900">客服中心</span>
            </div>
            <p className="text-xs text-foreground-400">
              客服在線：{onlineCount} / {onlineAgents.length} 位
            </p>
          </div>

          <div className="flex-1 overflow-y-auto px-3 py-2 space-y-1">
            {onlineAgents.map((agent) => (
              <div
                key={agent.id}
                className="flex items-center gap-3 px-3 py-2.5 rounded-lg hover:bg-background-100/80 transition-colors"
              >
                <div className="relative">
                  <div className="w-8 h-8 rounded-full bg-background-200 flex items-center justify-center text-foreground-500 text-xs font-bold">
                    {getInitial(agent.name)}
                  </div>
                  <span
                    className={`absolute -bottom-0.5 -right-0.5 w-3 h-3 rounded-full border-2 border-background-50 ${
                      agent.status === "online" ? "bg-primary-500" : "bg-foreground-300"
                    }`}
                  />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-foreground-800">{agent.name}</p>
                  <div className="flex items-center gap-1 mt-0.5">
                    {agent.languages.map((lang) => (
                      <span
                        key={lang}
                        className="text-[10px] px-1.5 py-0.5 rounded bg-background-200 text-foreground-500"
                      >
                        {lang}
                      </span>
                    ))}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>

      <style>{`
        @keyframes fadeInUp {
          from {
            opacity: 0;
            transform: translateY(16px);
          }
          to {
            opacity: 1;
            transform: translateY(0);
          }
        }
        @keyframes typingPulse {
          0%, 100% {
            opacity: 0.3;
            transform: scale(0.8);
          }
          50% {
            opacity: 1;
            transform: scale(1);
          }
        }
        @keyframes bumpIn {
          0% {
            background-color: oklch(var(--accent-100) / 0.8);
            transform: translateX(-4px);
          }
          50% {
            background-color: oklch(var(--accent-50) / 0.5);
            transform: translateX(2px);
          }
          100% {
            background-color: transparent;
            transform: translateX(0);
          }
        }
      `}</style>
    </div>
  );
}